// Storefront collections + Discovery Sets — pure helpers for the BO page
// (spec docs/specs/2026-10-13-www-nav-i18n-collections-design.md §3.3 / §5.2).
// Kept free of Vue so jest can cover them (this repo cannot mount .vue files).

export const MAX_ITEMS = 60;
export const NAME_MAX = 120;
export const DESC_MAX = 500;
export const SLUG_MIN = 2;
export const SLUG_MAX = 60;
export const RESERVED_SLUG = "discovery-sets";
// Stored names for a Discovery Sets record created from the BO for the first
// time (the www page falls back to these words anyway).
export const DISCOVERY_DEFAULT_NAMES = Object.freeze({ name_th: "เซตทดลอง", name_en: "Discovery Sets" });
export const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** Slug suggestion from the English name: lowercase ascii words joined by "-". */
export function slugify(name) {
	return String(name == null ? "" : name)
		.normalize("NFKD")
		.replace(/[̀-ͯ]/g, "")
		.toLowerCase()
		.replace(/['’]/g, "")
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, SLUG_MAX)
		.replace(/-+$/g, "");
}

/** "" when fine, else the API rule name: "required" | "format" | "reserved". */
export function slugProblem(slug) {
	const s = String(slug == null ? "" : slug).trim();
	if (!s) return "required";
	if (s.length < SLUG_MIN || s.length > SLUG_MAX || !SLUG_RE.test(s)) return "format";
	if (s === RESERVED_SLUG) return "reserved";
	return "";
}

/** Empty editor state. `kind` "discovery" has no slug field. */
export function emptyForm() {
	return {
		id: 0,
		slug: "",
		name_th: "",
		name_en: "",
		description_th: "",
		description_en: "",
		cover_key: "",
		cover_url: "",
		items: [],
		is_active: false,
		updated_at: ""
	};
}

/** Items as [{type,id}] — valid types/ids only, first occurrence of a (type,id) pair wins. */
export function normalizeItems(items) {
	const seen = {};
	const out = [];
	(Array.isArray(items) ? items : []).forEach((it) => {
		if (!it || (it.type !== "product" && it.type !== "bundle")) return;
		const id = Number(it.id);
		if (!Number.isInteger(id) || id <= 0) return;
		const k = it.type + ":" + id;
		if (seen[k]) return;
		seen[k] = true;
		out.push({ type: it.type, id });
	});
	return out;
}

/** API row (GET /collections/:id or /discovery-sets `data`) → editor state. */
export function formFromRow(row) {
	const f = emptyForm();
	if (!row || typeof row !== "object") return f;
	Object.keys(f).forEach((k) => {
		if (row[k] !== undefined && row[k] !== null) f[k] = row[k];
	});
	f.id = Number(row.id) || 0;
	f.is_active = !!row.is_active;
	f.items = normalizeItems(row.items);
	return f;
}

/** `unavailable` from a detail response → { products: number[], bundles: number[] }. */
export function unavailableFrom(data) {
	const u = (data && data.unavailable) || {};
	const ids = (v) => (Array.isArray(v) ? v.map(Number).filter((n) => n > 0) : []);
	return { products: ids(u.products), bundles: ids(u.bundles) };
}

/** True when this item was reported unavailable by the API. */
export function isUnavailable(item, unavailable) {
	if (!item || !unavailable) return false;
	const list = item.type === "bundle" ? unavailable.bundles : unavailable.products;
	return Array.isArray(list) && list.indexOf(item.id) !== -1;
}

/**
 * Client-side checks mirroring §3.3 so obvious mistakes never reach the API.
 * Returns { field: rule } using the API rule names (required | max_length |
 * format | reserved | too_many). `discovery` skips the slug.
 */
export function validateForm(form, { discovery = false } = {}) {
	const errs = {};
	const f = form || {};
	const len = (v) => String(v == null ? "" : v).length;
	if (!discovery) {
		const p = slugProblem(f.slug);
		if (p) errs.slug = p;
	}
	["name_th", "name_en"].forEach((k) => {
		const v = String(f[k] == null ? "" : f[k]).trim();
		if (!v) errs[k] = "required";
		else if (len(v) > NAME_MAX) errs[k] = "max_length";
	});
	["description_th", "description_en"].forEach((k) => {
		if (len(f[k]) > DESC_MAX) errs[k] = "max_length";
	});
	if ((Array.isArray(f.items) ? f.items.length : 0) > MAX_ITEMS) errs.items = "too_many";
	return errs;
}

/**
 * PUT/POST body. Never sends cover_url (the API ignores it anyway).
 * `withItems: false` omits `items` so the stored list is kept (quick status
 * toggle); `expected_updated_at` is the exact value from the last response.
 */
export function buildPayload(form, { discovery = false, withItems = true, expectedUpdatedAt = "" } = {}) {
	const f = form || {};
	const s = (v) => String(v == null ? "" : v).trim();
	const body = {
		name_th: s(f.name_th),
		name_en: s(f.name_en),
		description_th: s(f.description_th),
		description_en: s(f.description_en),
		cover_key: s(f.cover_key),
		is_active: !!f.is_active
	};
	if (!discovery) body.slug = s(f.slug);
	if (withItems) body.items = normalizeItems(f.items);
	if (expectedUpdatedAt) body.expected_updated_at = expectedUpdatedAt;
	return body;
}

/** Stable error code from an axios error ({code} or {error:{code}}). */
export function apiCode(err) {
	const d = err && err.response && err.response.data;
	return (d && (d.code || (d.error && d.error.code))) || "";
}

export function isSchemaMissing(err) {
	return apiCode(err) === "schema_missing" || !!(err && err.response && err.response.status === 503);
}

/**
 * Not allowed to write. mw.IsAdmin answers 403 since bo-api fix/role-403
 * (mdf-seller-fn#3); 401 is kept for an api still on the old gate — in practice
 * http.js turns a 401 into a logout before this runs.
 */
export function isForbidden(err) {
	const st = err && err.response && err.response.status;
	return st === 401 || st === 403;
}

/**
 * Field errors from a 400/409 envelope {code, errors:[{field, rule}]}.
 * Item paths like "items[3].type" collapse to "items". The top-level code
 * fills in when the list is empty (slug_taken / slug_reserved / too_many_items).
 */
export function fieldErrors(err) {
	const d = (err && err.response && err.response.data) || {};
	const out = {};
	(Array.isArray(d.errors) ? d.errors : []).forEach((e) => {
		if (!e || !e.field) return;
		const field = String(e.field).replace(/\[\d+\].*$/, "");
		if (!out[field]) out[field] = String(e.rule || "invalid");
	});
	const code = apiCode(err);
	if (code === "slug_taken" && !out.slug) out.slug = "taken";
	if (code === "slug_reserved" && !out.slug) out.slug = "reserved";
	if (code === "too_many_items" && !out.items) out.items = "too_many";
	return out;
}

/** i18n key (under storefront_collection.) for an API error code, or "". */
export function errorKey(code) {
	return {
		slug_taken: "err.slug_taken",
		slug_reserved: "err.slug_reserved",
		schema_missing: "err.schema_missing",
		stale: "err.stale",
		too_many_items: "err.too_many_items"
	}[code] || "";
}

/** Public www URL for a collection slug (or the discovery page). */
export function collectionUrl(origin, slug) {
	const o = String(origin || "").replace(/\/+$/, "");
	return slug ? `${o}/collections/${encodeURIComponent(slug)}` : `${o}/discovery-sets`;
}

/** Swap item i with i+dir; returns a new array (out-of-range → unchanged copy). */
export function moveItem(arr, i, dir) {
	const a = (arr || []).slice();
	const j = i + dir;
	if (i < 0 || i >= a.length || j < 0 || j >= a.length) return a;
	const t = a[i];
	a[i] = a[j];
	a[j] = t;
	return a;
}

/** Append (type,id) if absent and under the cap; returns a new array. */
export function addItem(arr, type, id) {
	const a = normalizeItems(arr);
	const n = Number(id);
	if (!(n > 0) || a.length >= MAX_ITEMS) return a;
	if (a.some((x) => x.type === type && x.id === n)) return a;
	return a.concat({ type, id: n });
}
